import {
  defaultUserMessage,
  errorCodeToUserMessage,
  hintCodeToUserUserMessage,
  isIOS,
  isIOSSafariOrAndroidChrome,
} from "./utils";

interface StartBankIdAuthOutput {
  orderRef: string;
  autoStartToken: string;
}

export const bankIdErrorStatusCode = 502;

export const backendBaseUrl = "/api";

export const finishBankIdLoginUrl = (login_challenge: string): string =>
  `${backendBaseUrl}/finish?login_challenge=${login_challenge}`;

export const startBankIdAuth = async (
  login_challenge: string,
  login_method:    string,
  personalNumber?: string,
): Promise<StartBankIdAuthOutput> => {
  const result = await fetch(`${backendBaseUrl}/se-bankid/start-auth`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      login_challenge,
      login_method,
      personalNumber,
    }),
  });
  if (!result.ok) {
    throw result;
  }
  return await result.json();
};

interface BankIdStatusOutput {
  status: "complete" | "pending" | "failed" | "not_started";
  errorCode: string;
  hintCode: string;
}

export const bankIdStatus = async (
  login_challenge: string,
  login_method:    string
): Promise<BankIdStatusOutput> => {
  const result = await fetch(`${backendBaseUrl}/se-bankid/status`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ 
      login_challenge,
      login_method 
    }),
  });
  if (!result.ok) {
    throw result;
  }
  return await result.json();
};

export const getAction = async (  login_challenge: string
): Promise<any> => {
  console.log("IN GET ACTION")
  const result = await fetch(`${backendBaseUrl}/se-bankid/action`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    }
  });
  if (!result.ok) {
    throw result;
  }
  console.log(result.json())
  return await result.json();
};

interface FinishBankIdLoginOutput {
  redirect_to: string;
}

export const finishBankIdLogin = async (
  login_challenge: string
): Promise<FinishBankIdLoginOutput> => {
  const result = await fetch(finishBankIdLoginUrl(login_challenge), {
    method: "POST",
  });
  if (!result.ok) {
    throw new Error("Error when finishing BankID login");
  }
  return await result.json();
};

interface GetQrCodeOutput {
  qrCode: string;
}

export const getQrCode = async (
  login_challenge: string
): Promise<GetQrCodeOutput | null> => {
  const result = await fetch(
    `${backendBaseUrl}/se-bankid/qrcode?login_challenge=${login_challenge}`
  );
  if (!result.ok) {
    throw result;
  }
  if (result.status === 204) {
    return null;
  }
  return await result.json();
};

export const cancelLogin = async (login_challenge: string): Promise<void> => {
  const result = await fetch(`${backendBaseUrl}/cancel`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ login_challenge }),
  });
  if (!result.ok) {
    throw new Error("Error when cancelling login");
  }
  return;
};

export const collectAndRedirect = async (
  login_challenge: string,
  login_method: string,
  pollingCallback: React.MutableRefObject<NodeJS.Timeout | null>,
  setLoginFailed: (loginFailed: boolean) => void,
  setUserMessage: (msg: string) => void,
  setQrCode?: (qrCode: string | null) => void
) => {
  try {
    const { status, hintCode } = await bankIdStatus(
      login_challenge,
      login_method
    );
    console.log("1.", status, hintCode )
    switch (status) {
      case "complete":
        const { redirect_to } = await finishBankIdLogin(login_challenge);
        console.log("2.", redirect_to )
        if (pollingCallback.current) {
          clearTimeout(pollingCallback.current);
          pollingCallback.current = null;
        }
        window.location.assign(redirect_to);
        break;
      case "pending":
        console.log("3.")
        if (setQrCode) {
          const getQrCodeOutput = await getQrCode(login_challenge);
          setQrCode(getQrCodeOutput?.qrCode ?? null);
        }
        pollingCallback.current = setTimeout(() => {
          collectAndRedirect(
            login_challenge,
            login_method,
            pollingCallback,
            setLoginFailed,
            setUserMessage,
            setQrCode
          );
        }, 1000);
        break;
      case "failed":
        console.log("4." )
        const userMessage = hintCodeToUserUserMessage(hintCode);
        setUserMessage(userMessage);
        setLoginFailed(true);
        break;
    }
  } catch (r) {
    console.log("5.", r )
    if ((r as Response).status === bankIdErrorStatusCode) {
      const { errorCode } = await (r as Response).json();
      if (errorCode !== undefined) {
        const userMessage = errorCodeToUserMessage(errorCode);
        setUserMessage(userMessage);
      } else {
        setUserMessage(defaultUserMessage);
      }
    } else {
      setUserMessage(defaultUserMessage);
    }
    setLoginFailed(true);
  }
};

export const startBankIdLogin = async (
  login_challenge: string,
  pollingCallback: React.MutableRefObject<NodeJS.Timeout | null>,
  login_method: "this_device" | "mobile",
  bankIdUrl: React.MutableRefObject<string>,
  setBankIdOpened: (opened: boolean) => void,
  setLoginFailed: (loginFailed: boolean) => void,
  setUserMessage: (msg: string) => void,
  setQrCode?: (qrCode: string | null) => void,
  personalNumber?: string,
) => {
  console.log("6.")
  if (login_challenge !== null) {
    try {
      // there might be an ongoing login already if user refreshed or is on iOS, try that first
      const { status, hintCode } = await bankIdStatus(
        login_challenge, 
        login_method
      );
      console.log("7.", status, hintCode )
      switch (status) {
        case "complete":
          if (login_method === "this_device") {
            setBankIdOpened(true);
          }
          const { redirect_to } = await finishBankIdLogin(login_challenge);
          console.log("8.", redirect_to)
          if (pollingCallback.current) {
            clearTimeout(pollingCallback.current);
            pollingCallback.current = null;
          }
          window.location.assign(redirect_to);
          break;
        case "pending":
          // if we have started using QR code, then button needs to be enabled
          // to start a new auth
          console.log("9.")
          if (
            login_method === "this_device" &&
            hintCode !== "outstandingTransaction"
          ) {
            setBankIdOpened(true);
          }
          if (setQrCode) {
            const getQrCodeOutput = await getQrCode(login_challenge);
            setQrCode(getQrCodeOutput?.qrCode ?? null);
          }
          if (!pollingCallback.current) {
            pollingCallback.current = setTimeout(() => {
              collectAndRedirect(
                login_challenge,
                login_method,
                pollingCallback,
                setLoginFailed,
                setUserMessage,
                setQrCode
              );
            }, 1000);
          }
          break;
        case "failed":
          console.log("10.")
          const userMessage = hintCodeToUserUserMessage(hintCode);
          setUserMessage(userMessage);
          setLoginFailed(true);
          break;
        case "not_started":
          const { autoStartToken } = await startBankIdAuth(
            login_challenge,
            login_method,
            personalNumber,
          );
          console.log("11.", autoStartToken)
          const redirectUrl = isIOS()
            ? encodeURIComponent(`${window.location.href}`)
            : "null";

          if (isIOSSafariOrAndroidChrome()) {
            bankIdUrl.current = `https://app.bankid.com/?autostarttoken=${autoStartToken}&redirect=${redirectUrl}`;
          } else {
            bankIdUrl.current = `bankid:///?autostarttoken=${autoStartToken}&redirect=${redirectUrl}`;
          }
          if (!pollingCallback.current) {
            pollingCallback.current = setTimeout(() => {
              collectAndRedirect(
                login_challenge,
                login_method,
                pollingCallback,
                setLoginFailed,
                setUserMessage,
                setQrCode
              );
            }, 1000);
          }
          break;
      }
    } catch (r) {
      console.log("12.", r)
      if ((r as Response).status === bankIdErrorStatusCode) {
        const { errorCode } = await (r as Response).json();
        if (errorCode !== undefined) {
          const userMessage = errorCodeToUserMessage(errorCode);
          setUserMessage(userMessage);
        } else {
          setUserMessage(defaultUserMessage);
        }
      } else {
        setUserMessage(defaultUserMessage);
      }
      setLoginFailed(true);
    }
  }
};

export const checkIfBankIdLoginStarted = async (
  login_challenge: string,
  pollingCallback: React.MutableRefObject<NodeJS.Timeout | null>,
  login_method: "this_device" | "mobile",
  setBankIdOpened: (opened: boolean) => void,
  setLoginFailed: (loginFailed: boolean) => void,
  setUserMessage: (msg: string) => void,
  setQrCode?: (qrCode: string | null) => void
) => {
  if (login_challenge !== null) {
    try {
      const { status, hintCode } = await bankIdStatus(
        login_challenge, 
        login_method
      );
      switch (status) {
        case "complete":
          if (login_method === "this_device") {
            setBankIdOpened(true);
          }
          const { redirect_to } = await finishBankIdLogin(login_challenge);
          if (pollingCallback.current) {
            clearTimeout(pollingCallback.current);
            pollingCallback.current = null;
          }
          window.location.assign(redirect_to);
          break;
        case "pending":
          if (
            login_method === "this_device" &&
            hintCode !== "outstandingTransaction"
          ) {
            setBankIdOpened(true);
          }
          if (!pollingCallback.current) {
            pollingCallback.current = setTimeout(() => {
              collectAndRedirect(
                login_challenge,
                login_method,
                pollingCallback,
                setLoginFailed,
                setUserMessage,
                setQrCode
              );
            }, 1000);
          }
          break;
        case "failed":
          const userMessage = hintCodeToUserUserMessage(hintCode);
          setUserMessage(userMessage);
          setLoginFailed(true);
          break;
        case "not_started":
          break;
      }
    } catch (r) {
      // no error handling. We see an error screen here when returning
      // to the login screen from the BankID app on Android/iOS - this works
      // around that
    }
  }
};
